import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'About Us', href: '#' },
    { label: 'Services', href: '/services' },
    { label: 'Departments', href: '/departments' },
    { label: 'Contact', href: '#contact' },
  ];

  const services = [
    { label: 'Emergency Care', href: '/services' },
    { label: 'General Medicine', href: '/services' },
    { label: 'ICU Services', href: '/services' },
    { label: 'OPD Services', href: '/services' },
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/share/19o3Fa6JV6/', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/nandedmultispeciality?igsh=aGw2ankxYmx5Nndt', label: 'Instagram' },
  ];

  return (
    <footer className="bg-[#1c202d] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"> {/* Reduced py-16 to py-12 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"> {/* Reduced gap-12 to gap-8 */}
          {/* Brand Section - Reduced spacing */}
          <div className="space-y-4"> {/* Reduced from space-y-6 to space-y-4 */}
            <div className="flex items-center">
              <img 
                src="/images/logomain.png" 
                alt="NANDED MULTISPECIALITY HOSPITAL"
                className="h-30 w-auto object-contain max-h-20" // h-30 with max-h-20
                onError={(e) => {
                  // Fallback if image doesn't load
                  e.target.style.display = 'none';
                  const fallback = document.getElementById('footer-logo-fallback');
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              {/* Fallback logo if image doesn't load */}
              <div id="footer-logo-fallback" className="hidden items-center space-x-3">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] flex items-center justify-center shadow-[var(--shadow-md)] flex-shrink-0">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-white leading-tight">
                    NANDED
                  </span>
                  <span className="text-xs text-white/70 leading-tight font-medium">
                    MULTISPECIALITY HOSPITAL
                  </span>
                </div>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed text-sm">
              Providing exceptional healthcare services with compassion and expertise
              since 2017. Your health is our priority.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  target='_blank'
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/10 hover:bg-[hsl(var(--primary))] flex items-center justify-center transition-all duration-300 hover:scale-110"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links - Adjusted spacing */}
          <div className="lg:mt-2"> {/* Added mt-2 to align with logo */}
            <h3 className="text-lg font-bold mb-4">Quick Links</h3> {/* Reduced mb-6 to mb-4 */}
            <ul className="space-y-2"> {/* Reduced space-y-3 to space-y-2 */}
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-white/70 hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {link.label}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - Adjusted spacing */}
          <div className="lg:mt-2"> {/* Added mt-2 to align with logo */}
            <h3 className="text-lg font-bold mb-4">Our Services</h3> {/* Reduced mb-6 to mb-4 */}
            <ul className="space-y-2"> {/* Reduced space-y-3 to space-y-2 */}
              {services.map((service, index) => (
                <li key={index}>
                  <a
                    href={service.href}
                    className="text-white/70 hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {service.label}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Adjusted spacing */}
          <div className="lg:mt-2"> {/* Added mt-2 to align with logo */}
            <h3 className="text-lg font-bold mb-4">Contact Us</h3> {/* Reduced mb-6 to mb-4 */}
            <ul className="space-y-3"> {/* Reduced space-y-4 to space-y-3 */}
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Emergency</p>
                  <a
                    href="tel:02067527445"
                    className="text-white/70 hover:text-[hsl(var(--primary))] transition-colors"
                  >
                    02067527445
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Email</p>
                  <a
                    href="mailto:Support@nandedhospitalpune.in"
                    className="text-white/70 hover:text-[hsl(var(--primary))] transition-colors"
                  >
                    Support@nandedhospitalpune.in
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Location</p> 
                  <a 
                    href="https://maps.app.goo.gl/6idruHNRKPYp8Em69?g_st=aw" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/70 hover:text-[hsl(var(--primary))] transition-colors"
                  >
                    <p className="text-white/70 text-sm leading-tight">
                      Nanded Hospital, Nirmala Heights, Near Tamilnad Mercantile Bank, 
                      Nandedgaon, Pune, Maharashtra 411068
                    </p>
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar - Reduced spacing */}
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"> {/* Reduced mt-12 to mt-8 and pt-8 to pt-6 */}
          <p className="text-white/70 text-sm">
            © {currentYear} NANDED MULTISPECIALITY HOSPITAL. All rights reserved.
          </p>
          <div className="flex space-x-6 text-sm">
            <a href="#" className="text-white/70 hover:text-[hsl(var(--primary))] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-white/70 hover:text-[hsl(var(--primary))] transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};